// `applies_to` glob matching — ontwerp §3.6, §7.1 (`kb_for_paths`) and §8.
//
// The design names picomatch; the version npm resolves in this repo (2.x)
// `require`s Node's `path` and reads `process`, which breaks in the browser
// bundle @goblic/web builds from this package. This is a small, dependency-
// free matcher for the subset `applies_to` needs:
//
//   `**`      a whole segment: any number of path segments, including none
//             (`a/**/b` matches `a/b`); `a**b` inside a segment acts like `*`
//   `*`       any characters within one segment (never '/')
//   `?`       one character within a segment
//   `[abc]`   a character class (`[!abc]` / `[^abc]` negated, ranges `a-z`)
//   `{a,b}`   alternatives (may contain the other wildcards, no nesting)
//
// Additions for convenience, both documented in the contract:
// - a pattern without any wildcard also matches everything below it as a
//   folder (`packages/api` ≈ `packages/api/**`, besides the file itself);
// - a trailing '/' means "this folder" (`packages/api/` ≈ `packages/api/**`).
// Paths and patterns are compared after stripping a leading './' or '/'.
// Matching is case-sensitive, like git paths.
//
// Security (page text is written by agents too): no regular expressions are
// built from patterns. A pattern compiles to segment lists; matching is a
// DP over segments with an iterative, non-backtracking wildcard matcher per
// segment, so time is bounded by pattern length × path length. Brace
// expansion is capped at `KB_GLOB_MAX_EXPANSIONS` alternatives in total,
// pattern length at `KB_GLOB_MAX_LENGTH`. A pattern that breaks a rule
// (nested braces, a reversed range like `[z-a]`, too many alternatives)
// does not compile: `kbGlobError` names the problem, the write is refused,
// and the matchers treat such a pattern (from older data) as matching
// nothing.

/** Max characters in one `applies_to` pattern. */
export const KB_GLOB_MAX_LENGTH = 300

/** Max alternatives one pattern may expand to through `{a,b}` groups (product over all groups). */
export const KB_GLOB_MAX_EXPANSIONS = 32

/** Paths longer than this never match (bounds the per-path matching cost). */
export const KB_GLOB_MAX_PATH_LENGTH = 1024

type CharToken =
  | { kind: 'char'; char: string }
  | { kind: 'any' }
  | { kind: 'star' }
  | { kind: 'class'; negate: boolean; ranges: [string, string][] }

/** One segment between '/'s: `**` (any number of segments) or a token list. */
type Segment = { kind: 'globstar' } | { kind: 'tokens'; tokens: CharToken[] }

/** A compiled pattern: one or more alternatives (after `{}` expansion), each a segment list. */
export interface CompiledKbGlob {
  alternatives: Segment[][]
}

/** Thrown by `compileKbGlob` for a pattern that breaks a rule above. */
export class KbGlobError extends Error {
  constructor(
    readonly pattern: string,
    reason: string,
  ) {
    super(`Invalid applies_to pattern "${pattern.length > 60 ? `${pattern.slice(0, 57)}...` : pattern}": ${reason}`)
    this.name = 'KbGlobError'
  }
}

function cleanPath(value: string): string {
  return value.trim().replace(/\\/g, '/').replace(/^(\.\/)+/, '').replace(/^\/+/, '')
}

// ---------------------------------------------------------------------------
// Brace expansion (bounded, no nesting)
// ---------------------------------------------------------------------------

interface BraceGroup {
  start: number
  end: number
  options: string[]
}

/** Whether the '{' at `open` is closed by a matching '}' when braces are counted as nested. */
function closesAround(pattern: string, open: number): boolean {
  let depth = 0
  for (let k = open; k < pattern.length; k += 1) {
    if (pattern[k] === '{') depth += 1
    else if (pattern[k] === '}') {
      depth -= 1
      if (depth === 0) return true
    }
  }
  return false
}

/** The `{a,b}` groups of `pattern`, left to right. `{x}` (no comma), an unclosed `{` and a stray `{` before a group stay literal. */
function braceGroups(pattern: string, original: string): BraceGroup[] {
  const groups: BraceGroup[] = []
  let i = 0
  while (i < pattern.length) {
    if (pattern[i] !== '{') {
      i += 1
      continue
    }
    let j = i + 1
    let closed = -1
    let reopened = -1
    while (j < pattern.length) {
      const c = pattern[j]
      if (c === '{') {
        // Only nesting when this '{' still closes around the inner group
        // (`{a,{b,c}}`); a stray '{' before a group (`a{b/{c,d}`) is literal.
        if (closesAround(pattern, i)) throw new KbGlobError(original, 'nested {…} groups are not supported')
        reopened = j
        break
      }
      if (c === '}') {
        closed = j
        break
      }
      j += 1
    }
    if (reopened !== -1) {
      i = reopened
      continue
    }
    if (closed === -1) break // unclosed: the rest is literal
    const body = pattern.slice(i + 1, closed)
    if (body.includes(',')) groups.push({ start: i, end: closed, options: body.split(',') })
    i = closed + 1
  }
  return groups
}

function expandBraces(pattern: string, original: string): string[] {
  const groups = braceGroups(pattern, original)
  let total = 1
  for (const group of groups) {
    total *= group.options.length
    if (total > KB_GLOB_MAX_EXPANSIONS) {
      throw new KbGlobError(original, `{…} alternatives expand to more than ${KB_GLOB_MAX_EXPANSIONS} patterns`)
    }
  }
  let results = ['']
  let last = 0
  for (const group of groups) {
    const head = pattern.slice(last, group.start)
    results = results.flatMap((prefix) => group.options.map((option) => `${prefix}${head}${option}`))
    last = group.end + 1
  }
  const tail = pattern.slice(last)
  return results.map((prefix) => `${prefix}${tail}`)
}

// ---------------------------------------------------------------------------
// Tokenizing one segment
// ---------------------------------------------------------------------------

/** Parses `[...]` at `start`; `null` when there is no closing ']' (then '[' is literal). */
function parseClass(segment: string, start: number, original: string): { token: CharToken; next: number } | null {
  let i = start + 1
  let negate = false
  if (segment[i] === '!' || segment[i] === '^') {
    negate = true
    i += 1
  }
  const bodyStart = i
  // A ']' right after '[' / '[!' is a literal member.
  if (segment[i] === ']') i += 1
  const close = segment.indexOf(']', i)
  if (close === -1) return null
  const body = segment.slice(bodyStart, close)
  if (body === '') return null
  const ranges: [string, string][] = []
  for (let k = 0; k < body.length; k += 1) {
    const from = body[k]!
    if (body[k + 1] === '-' && k + 2 < body.length) {
      const to = body[k + 2]!
      if (from > to) throw new KbGlobError(original, `character range [${from}-${to}] is reversed`)
      ranges.push([from, to])
      k += 2
    } else {
      ranges.push([from, from])
    }
  }
  return { token: { kind: 'class', negate, ranges }, next: close + 1 }
}

function tokenizeSegment(segment: string, original: string): { tokens: CharToken[]; wildcard: boolean } {
  const tokens: CharToken[] = []
  let wildcard = false
  let i = 0
  while (i < segment.length) {
    const char = segment[i]!
    if (char === '*') {
      wildcard = true
      // `a**b` behaves like `*`; consecutive stars collapse into one.
      if (tokens[tokens.length - 1]?.kind !== 'star') tokens.push({ kind: 'star' })
      i += 1
      continue
    }
    if (char === '?') {
      wildcard = true
      tokens.push({ kind: 'any' })
      i += 1
      continue
    }
    if (char === '[') {
      const parsed = parseClass(segment, i, original)
      if (parsed) {
        wildcard = true
        tokens.push(parsed.token)
        i = parsed.next
        continue
      }
    }
    tokens.push({ kind: 'char', char })
    i += 1
  }
  return { tokens, wildcard }
}

function compileAlternative(alternative: string, original: string): Segment[] {
  const segments: Segment[] = []
  let wildcard = false
  for (const part of alternative.split('/')) {
    if (part === '**') {
      wildcard = true
      // `**/**` is the same as `**`.
      if (segments[segments.length - 1]?.kind !== 'globstar') segments.push({ kind: 'globstar' })
      continue
    }
    const { tokens, wildcard: has } = tokenizeSegment(part, original)
    wildcard ||= has
    segments.push({ kind: 'tokens', tokens })
  }
  // Plain (wildcard-free) pattern: the file itself or anything below it.
  if (!wildcard) segments.push({ kind: 'globstar' })
  return segments
}

// ---------------------------------------------------------------------------
// Compile / validate
// ---------------------------------------------------------------------------

const cache = new Map<string, CompiledKbGlob>()

/** Compiles one `applies_to` pattern. Throws `KbGlobError` when it breaks a rule (see the header). */
export function compileKbGlob(pattern: string): CompiledKbGlob {
  const cached = cache.get(pattern)
  if (cached) return cached

  if (pattern.length > KB_GLOB_MAX_LENGTH) {
    throw new KbGlobError(pattern, `longer than ${KB_GLOB_MAX_LENGTH} characters`)
  }
  let clean = cleanPath(pattern)
  if (clean === '') throw new KbGlobError(pattern, 'empty pattern')
  if (clean.endsWith('/')) clean = `${clean}**`
  const compiled: CompiledKbGlob = {
    alternatives: expandBraces(clean, pattern).map((alternative) => compileAlternative(alternative, pattern)),
  }
  if (cache.size >= 1000) cache.clear()
  cache.set(pattern, compiled)
  return compiled
}

/** Why `pattern` is not a valid `applies_to` glob, or `null` when it is. */
export function kbGlobError(pattern: string): string | null {
  try {
    compileKbGlob(pattern)
    return null
  } catch (error) {
    return (error as Error).message
  }
}

// ---------------------------------------------------------------------------
// Matching
// ---------------------------------------------------------------------------

function charMatches(token: CharToken, char: string): boolean {
  switch (token.kind) {
    case 'char':
      return token.char === char
    case 'any':
      return true
    case 'class': {
      const inClass = token.ranges.some(([from, to]) => char >= from && char <= to)
      return token.negate ? !inClass : inClass
    }
    default:
      return false
  }
}

/**
 * Wildcard match of one segment, iterative (the classic two-pointer
 * algorithm: on a mismatch, resume after the last `*`). Never recurses or
 * backtracks further than one star, so it is O(tokens × text) at worst.
 */
function segmentMatches(tokens: readonly CharToken[], text: string): boolean {
  let t = 0
  let s = 0
  let starToken = -1
  let starText = 0
  while (s < text.length) {
    const token = tokens[t]
    if (token && token.kind === 'star') {
      starToken = t
      starText = s
      t += 1
      continue
    }
    if (token && charMatches(token, text[s]!)) {
      t += 1
      s += 1
      continue
    }
    if (starToken !== -1) {
      t = starToken + 1
      starText += 1
      s = starText
      continue
    }
    return false
  }
  while (t < tokens.length && tokens[t]!.kind === 'star') t += 1
  return t === tokens.length
}

/** DP over segments: `reach[j]` = the first `i` pattern segments can match the first `j` path segments. */
function segmentsMatch(pattern: readonly Segment[], path: readonly string[]): boolean {
  let reach = new Array<boolean>(path.length + 1).fill(false)
  reach[0] = true
  for (const segment of pattern) {
    const next = new Array<boolean>(path.length + 1).fill(false)
    if (segment.kind === 'globstar') {
      let any = false
      for (let j = 0; j <= path.length; j += 1) {
        any ||= reach[j]!
        next[j] = any
      }
    } else {
      for (let j = 0; j < path.length; j += 1) {
        if (reach[j] && segmentMatches(segment.tokens, path[j]!)) next[j + 1] = true
      }
    }
    reach = next
    if (!reach.includes(true)) return false
  }
  return reach[path.length]!
}

function matchesCompiled(compiled: CompiledKbGlob, cleanedPath: string): boolean {
  if (cleanedPath.length > KB_GLOB_MAX_PATH_LENGTH) return false
  const segments = cleanedPath.split('/')
  return compiled.alternatives.some((alternative) => segmentsMatch(alternative, segments))
}

/** Compiled pattern, or `null` for an empty or invalid one (which then matches nothing). */
function tryCompile(pattern: string): CompiledKbGlob | null {
  if (pattern.trim() === '') return null
  try {
    return compileKbGlob(pattern)
  } catch {
    return null
  }
}

/** Whether `path` (repository-relative) matches the glob `pattern`. An invalid pattern matches nothing. */
export function matchesKbGlob(path: string, pattern: string): boolean {
  const compiled = tryCompile(pattern)
  return compiled ? matchesCompiled(compiled, cleanPath(path)) : false
}

/** Whether `path` matches at least one of `patterns`. */
export function matchesAnyKbGlob(path: string, patterns: readonly string[]): boolean {
  return patterns.some((pattern) => matchesKbGlob(path, pattern))
}

/** The subset of `paths` that match at least one of `patterns` (invalid patterns are skipped). */
export function kbPathsMatching(paths: readonly string[], patterns: readonly string[]): string[] {
  const compiled = patterns.map(tryCompile).filter((c): c is CompiledKbGlob => c !== null)
  if (compiled.length === 0) return []
  return paths.filter((path) => {
    const clean = cleanPath(path)
    return compiled.some((c) => matchesCompiled(c, clean))
  })
}
